import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  InputAdornment,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  "pk.eyJ1IjoidHlsZXJnYWtzdGF0dGVyIiwiYSI6ImNsbnBoc2FwMDBtMnIybXBnanExZGJrdDUifQ.jQBgPfpgB0dQGfRLLCF73g";

const statusColors = {
  ONLINE: "#22c55e",
  OFFLINE: "#ef4444",
};

const DeviceMap = ({
  devices,
  height = 350,
  editable = false,
  onLocationSelect,
}) => {
  const mapRef = useRef();
  const mapContainerRef = useRef();
  const [markers, setMarkers] = useState([]);
  const editMarkerRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const searchTimerRef = useRef(null);
  const hasFittedBoundsRef = useRef(false);

  useEffect(() => {
    mapboxgl.accessToken = MAPBOX_TOKEN;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [-95.006, 42.7128],
      zoom: 3,
    });

    mapRef.current.addControl(new mapboxgl.NavigationControl(), "top-right");

    return () => {
      mapRef.current.remove();
    };
  }, []);

  // Geocoding search
  const handleSearch = (query) => {
    setSearchQuery(query);
    clearTimeout(searchTimerRef.current);

    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }

    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            query
          )}.json?access_token=${MAPBOX_TOKEN}&limit=5`
        );
        const data = await res.json();
        setSearchResults(data.features || []);
      } catch {
        setSearchResults([]);
      }
    }, 300);
  };

  const handleSelectResult = (result) => {
    const [lng, lat] = result.center;

    mapRef.current.flyTo({
      center: [lng, lat],
      zoom: 18,
      essential: true,
      speed: 3,
    });

    // Place edit marker at the searched location
    if (editMarkerRef.current) {
      editMarkerRef.current.remove();
    }

    editMarkerRef.current = new mapboxgl.Marker({
      color: "#f59e0b",
      draggable: true,
    })
      .setLngLat([lng, lat])
      .addTo(mapRef.current);

    if (onLocationSelect) {
      onLocationSelect(lat.toFixed(8), lng.toFixed(8));
    }

    editMarkerRef.current.on("dragend", () => {
      const pos = editMarkerRef.current.getLngLat();
      if (onLocationSelect) {
        onLocationSelect(pos.lat.toFixed(8), pos.lng.toFixed(8));
      }
    });

    setSearchQuery(result.place_name);
    setSearchResults([]);
  };

  // Clear search when leaving edit mode
  useEffect(() => {
    if (!editable) {
      setSearchQuery("");
      setSearchResults([]);
    }
  }, [editable]);

  // Handle click-to-place when editable
  const handleMapClick = useCallback(
    (e) => {
      if (!editable || !onLocationSelect) return;

      const { lng, lat } = e.lngLat;

      if (editMarkerRef.current) {
        editMarkerRef.current.remove();
      }

      editMarkerRef.current = new mapboxgl.Marker({
        color: "#f59e0b",
        draggable: true,
      })
        .setLngLat([lng, lat])
        .addTo(mapRef.current);

      onLocationSelect(lat.toFixed(8), lng.toFixed(8));

      editMarkerRef.current.on("dragend", () => {
        const pos = editMarkerRef.current.getLngLat();
        onLocationSelect(pos.lat.toFixed(8), pos.lng.toFixed(8));
      });
    },
    [editable, onLocationSelect]
  );

  useEffect(() => {
    if (!mapRef.current) return;

    mapRef.current.on("click", handleMapClick);
    return () => {
      mapRef.current.off("click", handleMapClick);
    };
  }, [handleMapClick]);

  // Clean up edit marker when leaving edit mode
  useEffect(() => {
    if (!editable && editMarkerRef.current) {
      editMarkerRef.current.remove();
      editMarkerRef.current = null;
    }
  }, [editable]);

  // Update cursor style when editable
  useEffect(() => {
    if (!mapRef.current) return;
    const canvas = mapRef.current.getCanvas();
    canvas.style.cursor = editable ? "crosshair" : "";
  }, [editable]);

  useEffect(() => {
    if (!mapRef.current) return;

    let newMarkers = [];
    let coordinates = [];

    markers?.forEach((marker) => marker.remove());

    let deviceList = devices;
    if (!Array.isArray(devices)) {
      deviceList = devices ? [devices] : [];
    }

    deviceList?.forEach((device) => {
      if (device?.lat?.length > 1 && device?.lng?.length > 1) {
        coordinates.push([device.lng, device.lat]);

        const color = statusColors[device.status] || statusColors.OFFLINE;

        const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(
          `<div style="min-width:150px;font-family:sans-serif;">
            <strong>${device.name || device.serial_number}</strong><br/>
            <span style="color:${color};">&#9679;</span> ${
            device.status || "Unknown"
          }<br/>
            <small style="color:#666;">${device.location || ""}</small>
          </div>`
        );

        const marker = new mapboxgl.Marker({ color })
          .setLngLat([device.lng, device.lat])
          .setPopup(popup)
          .addTo(mapRef.current);

        newMarkers.push(marker);
      }
    });

    if (!hasFittedBoundsRef.current && coordinates.length > 0) {
      hasFittedBoundsRef.current = true;
      if (coordinates.length > 1) {
        const bounds = coordinates.reduce(
          (b, coord) => b.extend(coord),
          new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
        );
        mapRef.current.fitBounds(bounds, { padding: 50 });
      } else {
        mapRef.current.flyTo({
          center: coordinates[0],
          essential: true,
          zoom: 10,
        });
      }
    }

    setMarkers(newMarkers);
  }, [devices]);

  return (
    <Box
      sx={{
        height,
        borderRadius: 2,
        overflow: "hidden",
        mb: 3,
        position: "relative",
      }}
    >
      {editable && (
        <>
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 2,
              width: 300,
            }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search address or city..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: "white",
                borderRadius: 1,
                "& .MuiOutlinedInput-root": { bgcolor: "white" },
              }}
            />
            {searchResults.length > 0 && (
              <Paper sx={{ mt: 0.5, maxHeight: 200, overflow: "auto" }}>
                <List dense disablePadding>
                  {searchResults.map((result) => (
                    <ListItemButton
                      key={result.id}
                      onClick={() => handleSelectResult(result)}
                    >
                      <ListItemText
                        primary={result.text}
                        secondary={result.place_name}
                        secondaryTypographyProps={{ noWrap: true }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 316,
              zIndex: 2,
              bgcolor: "warning.main",
              color: "warning.contrastText",
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              fontSize: 12,
              fontWeight: "bold",
            }}
          >
            Click map to set location
          </Box>
        </>
      )}
      <div
        style={{ height: "100%" }}
        ref={mapContainerRef}
        className="map-container"
      />
    </Box>
  );
};

export default DeviceMap;
